import * as orm from "@client/features/orm";
import * as forms from "@client/forms";
import * as router from "@client/router";
import * as typography from "@client/shared/typography";
import {server} from "@reactivated";
import React from "react";

const styles = {
    planner: "space-y-2.5",
    spacer: "space-y-2.5",
    grid: "planner-grid gap-2.5 flex flex-wrap",
} as const;

const DAYS_OF_WEEK_BY_INDEX = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

type TargetState = server.journal.day.TargetInput.input;

const TARGET_DEFAULTS: TargetState = {
    id: null,
    name: "",
    calories_target: null,
    protein_target: null,
    fat_target: null,
    carbs_target: null,
    net_carbs_target: null,
};

type TargetForms = React.MutableRefObject<
    Record<number, forms.SubmittableForm<unknown>>
>;

const TargetFormFields = ({
    target,
    targetIndex,
    setTargets,
    targetForms,
}: {
    target: TargetState;
    targetIndex: number;
    setTargets: React.Dispatch<React.SetStateAction<TargetState[]>>;
    targetForms: TargetForms;
}) => {
    const form = forms.useForm(server.journal.day.TargetInput, {
        value: target,
        onChange: (values) => {
            setTargets((prev) =>
                prev.map((t, i) => (i === targetIndex ? {...t, ...values} : t)),
            );
        },
    });
    // The page's Save composes one payload from every target, so it asks each
    // form whether it is ready through this registry.
    React.useEffect(() => {
        targetForms.current[targetIndex] = form;
        return () => {
            delete targetForms.current[targetIndex];
        };
    });

    return <forms.Fields form={form} />;
};

type Assignment = {reference: number | null};

const AssignmentField = ({
    index,
    assignment,
    references,
    setAssignments,
}: {
    index: number;
    assignment: Assignment;
    references: readonly (readonly [number | null, string])[];
    setAssignments: React.Dispatch<React.SetStateAction<Assignment[]>>;
}) => {
    const field = forms.useField(
        {
            kind: "select",
            widget: "select",
            name: `assignment_${index}`,
            options: references,
        },
        {
            value: assignment.reference,
            onChange: (reference) =>
                setAssignments((prev) =>
                    prev.map((a, i) => (i === index ? {...a, reference} : a)),
                ),
        },
    );

    return (
        <forms.Label label={DAYS_OF_WEEK_BY_INDEX[index]} error={null}>
            <forms.Widget field={field} />
        </forms.Label>
    );
};

export const Planner = router.Planner.withHooks(({profile}) => {
    const initialTargets: TargetState[] = profile.user.targets.map((t) => ({
        id: t.id,
        name: t.name,
        calories_target: t.calories_target,
        protein_target: t.protein_target,
        fat_target: t.fat_target,
        carbs_target: t.carbs_target,
        net_carbs_target: t.net_carbs_target,
    }));

    const initialAssignments = [...Array(7).keys()].map((dayOfWeek): Assignment => {
        const referenceIndex = initialTargets.findIndex(
            (t) => t.id === profile.assignments[dayOfWeek].target_id,
        );
        return {reference: referenceIndex === -1 ? null : referenceIndex};
    });

    const [targets, setTargets] = React.useState(initialTargets);
    const [assignments, setAssignments] = React.useState(initialAssignments);
    const targetForms: TargetForms = React.useRef({});

    return {
        targets,
        setTargets,
        targetForms,
        assignments,
        setAssignments,
    };
})
    .withIndex((props) => {
        const references: readonly (readonly [number | null, string])[] = [
            [null, "No target"],
            ...props.targets.map(
                (target, index) =>
                    [
                        index,
                        target.name === "" ? `<Target ${index}>` : target.name,
                    ] as const,
            ),
        ];

        return (
            <div className={styles.planner}>
                <typography.Title>Targets</typography.Title>
                {props.targets.length > 0 ? (
                    <div className={styles.grid}>
                        {props.targets.map((target, index) => (
                            <div
                                key={index}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 10,
                                }}
                            >
                                <TargetFormFields
                                    target={target}
                                    targetIndex={index}
                                    setTargets={props.setTargets}
                                    targetForms={props.targetForms}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <typography.P>
                        Add a target and assign it to days of the week to get started.
                    </typography.P>
                )}

                <typography.Title>Schedule</typography.Title>
                <div className={styles.grid}>
                    {props.assignments.map((assignment, index) => (
                        <div key={index}>
                            <AssignmentField
                                index={index}
                                assignment={assignment}
                                references={references}
                                setAssignments={props.setAssignments}
                            />
                        </div>
                    ))}
                    <div /> {/* I am a fake item to make the flex grid have 8 items */}
                </div>
            </div>
        );
    })
    .withActions((props) => [
        {
            icon: "calculator",
            title: "Calculator",
            highlight: props.targets.length === 0,
            link: router.reverse("calculator.index", {}),
        },
        {
            icon: "eraser",
            title: "Clear",
            onClick: async () => {
                const response = await server.journal.day.target_planner({
                    targets: [],
                    assignments: props.assignments.map(() => ({reference: null})),
                });

                if (response.type === "success") {
                    props.setTargets([]);
                    props.setAssignments((prev) =>
                        prev.map((a) => ({...a, reference: null})),
                    );
                    props.notify("Targets and schedule cleared");
                    void props.dispatch(orm.fetchProfile());
                }
            },
        },
        {
            icon: "plus",
            title: "Add Target",
            highlight: props.targets.length === 0,
            onClick: () => {
                props.setTargets((prev) => [...prev, TARGET_DEFAULTS]);
            },
        },
        {
            icon: "save",
            title: "Save",
            onClick: async () => {
                if (!forms.ready(...Object.values(props.targetForms.current))) {
                    return;
                }
                const response = await server.journal.day.target_planner({
                    targets: props.targets,
                    assignments: props.assignments,
                });

                if (response.type === "success") {
                    void props.analytics.track("SAVED_TARGET_ASSIGNMENTS");
                    await props.dispatch(orm.fetchProfile());
                    props.notify("Targets and schedule saved");
                }
            },
        },
    ])
    .withOptions(() => ({title: "Planner"}));
